/**
 * Configuración centralizada de opciones de filtros para Invoices/Orders
 * Este archivo define las opciones de filtros que se utilizan tanto en AdvancedFilter
 * como en PrefacturarModal para mantener consistencia en toda la aplicación.
 */
import { FilterStructure } from '@shared/components/ui/AdvancedFilter';

export type LoadColumnValuesCallback = (
  columnValue: string,
) => Promise<Array<{ label: string; value: string | number }>>;

export interface CountryOption {
  id: string;
  name: string;
  iso2: string;
}

const buildBasicActions = (t: (key: string) => string) => ({
  in: { label: t('filters.in', 'En'), value: 'in' },
  notIn: { label: t('filters.notIn', 'No en'), value: 'not_in' },
  isNull: { label: t('filters.isNull', 'Es nulo'), value: 'is_null' },
  isNotNull: { label: t('filters.isNotNull', 'No es nulo'), value: 'is_not_null' },
  equals: { label: t('filters.equals', 'Igual a'), value: 'equals' },
  notEquals: { label: t('filters.notEquals', 'Distinto de'), value: 'not_equals' },
  contains: { label: t('filters.contains', 'Contiene'), value: 'contains' },
  notContains: { label: t('filters.notContains', 'No contiene'), value: 'not_contains' },
  startsWith: { label: t('filters.startsWith', 'Empieza con'), value: 'starts_with' },
  endsWith: { label: t('filters.endsWith', 'Termina con'), value: 'ends_with' },
  greaterThan: { label: t('filters.greaterThan', 'Mayor que'), value: 'greater_than' },
  lessThan: { label: t('filters.lessThan', 'Menor que'), value: 'less_than' },
  greaterEqual: { label: t('filters.greaterEqual', 'Mayor o igual que'), value: 'greater_equal' },
  lessEqual: { label: t('filters.lessEqual', 'Menor o igual que'), value: 'less_equal' },
});

export const getViewPreInvoicesFilterOptions = (
  t: (key: string) => string,
  loadColumnValues: LoadColumnValuesCallback,
  showClientFilter = true,
  countries: CountryOption[] = [],
): FilterStructure[] => {
  const actions = buildBasicActions(t);
  const countryOptions = Array.isArray(countries) ? countries : [];

  return [
    {
      column: {
        label: t('filters.paymentMethodId', 'Método de pago'),
        value: 'payment_method_id',
        type: 'select',
      },
      actions: [actions.equals, actions.notEquals, actions.in, actions.notIn, actions.isNull, actions.isNotNull],
      values: [],
      loadValues: loadColumnValues,
    },
    {
      column: {
        label: t('filters.shipmentId', 'Shipment'),
        value: 'shipment_id',
        type: 'number',
      },
      actions: [actions.in, actions.notIn, actions.isNull, actions.isNotNull],
      values: null,
    },
    {
      column: {
        label: t('filters.shopId', 'Tienda'),
        value: 'shop_id',
        type: 'select',
      },
      actions: [actions.in, actions.notIn, actions.isNull, actions.isNotNull],
      values: [],
      loadValues: loadColumnValues,
    },
    ...(showClientFilter
      ? [
          {
            column: {
              label: t('filters.teamId', 'Equipo'),
              value: 'team_id',
              type: 'select',
            },
            actions: [actions.in, actions.notIn, actions.isNull, actions.isNotNull],
            values: [],
            loadValues: loadColumnValues,
          },
        ]
      : []),
    {
      column: {
        label: t('filters.courierId', 'Courier'),
        value: 'courier_id',
        type: 'select',
      },
      actions: [actions.in, actions.notIn, actions.isNull, actions.isNotNull],
      values: [],
      loadValues: loadColumnValues,
    },
    {
      column: {
        label: t('filters.type', 'Tipo'),
        value: 'type',
        type: 'select',
      },
      actions: [actions.in, actions.notIn, actions.isNull, actions.isNotNull],
      values: [
        { label: t('table.type_shipping', 'Envío'), value: 'SHIPPING' },
        { label: t('table.type_return', 'Devolución'), value: 'RETURN' },
        { label: t('table.type_reshipment', 'Reenvío'), value: 'RESHIPMENT' },
      ],
    },
    {
      column: {
        label: t('filters.createdAt', 'Fecha de creación'),
        value: 'created_at',
        type: 'date',
      },
      actions: [
        actions.in,
        actions.notIn,
        actions.isNull,
        actions.isNotNull,
        actions.greaterThan,
        actions.lessThan,
        actions.greaterEqual,
        actions.lessEqual,
      ],
      values: [],
    },
    {
      column: {
        label: t('filters.shippingDate', 'Fecha de envío'),
        value: 'shipping_date',
        type: 'date',
      },
      actions: [
        actions.in,
        actions.notIn,
        actions.isNull,
        actions.isNotNull,
        actions.greaterThan,
        actions.lessThan,
        actions.greaterEqual,
        actions.lessEqual,
      ],
      values: [],
    },
    {
      column: {
        label: t('filters.country', 'País'),
        value: 'country',
        type: 'select',
      },
      actions: [
        actions.equals,
        actions.notEquals,
        actions.contains,
        actions.notContains,
        actions.startsWith,
        actions.endsWith,
        actions.in,
        actions.notIn,
        actions.isNull,
        actions.isNotNull,
      ],
      values: countryOptions.map((country) => ({
        label: country.name,
        value: country.iso2,
      })),
    },
    {
      column: {
        label: t('filters.city', 'Ciudad'),
        value: 'city',
        type: 'text',
      },
      actions: [
        actions.equals,
        actions.notEquals,
        actions.contains,
        actions.notContains,
        actions.startsWith,
        actions.endsWith,
        actions.in,
        actions.notIn,
        actions.isNull,
        actions.isNotNull,
      ],
      values: null,
    },
    {
      column: {
        label: t('filters.zip', 'Código postal'),
        value: 'zip',
        type: 'text',
      },
      actions: [
        actions.equals,
        actions.notEquals,
        actions.contains,
        actions.notContains,
        actions.startsWith,
        actions.endsWith,
        actions.in,
        actions.notIn,
        actions.isNull,
        actions.isNotNull,
      ],
      values: null,
    },
    {
      column: {
        label: t('filters.numItems', 'N.º ítems'),
        value: 'num_items',
        type: 'number',
      },
      actions: [actions.in, actions.notIn, actions.isNull, actions.isNotNull],
      values: null,
    },
    {
      column: {
        label: t('filters.period', 'Período'),
        value: 'period',
        type: 'select',
      },
      actions: [actions.in, actions.notIn, actions.isNull, actions.isNotNull],
      values: [
        { label: t('filters.periodOptions.daily', 'Diario'), value: 'DAILY' },
        { label: t('filters.periodOptions.weekly', 'Semanal'), value: 'WEEKLY' },
        { label: t('filters.periodOptions.monthly', 'Mensual'), value: 'MONTHLY' },
        { label: t('filters.periodOptions.quarterly', 'Trimestral'), value: 'QUARTERLY' },
        { label: t('filters.periodOptions.annual', 'Anual'), value: 'ANNUAL' },
      ],
    },
    {
      column: {
        label: t('filters.financialStatus', 'Estado financiero'),
        value: 'financial_status',
        type: 'select',
      },
      actions: [actions.in, actions.notIn, actions.isNull, actions.isNotNull],
      values: [
        { label: t('filters.financialStatusOptions.notInvoiced', 'No facturado'), value: 'NOT_INVOICED' },
        { label: t('filters.financialStatusOptions.preinvoiced', 'Prefacturado'), value: 'PREINVOICED' },
        { label: t('filters.financialStatusOptions.invoiced', 'Facturado'), value: 'INVOICED' },
        { label: t('filters.financialStatusOptions.blocked', 'Bloqueado'), value: 'BLOCKED' },
      ],
    },
  ];
};
